// Pastel Color Palette for MNUbot
export const colors = {
  // Primary Colors - Soft Blue
  primary: {
    50: '#f0f8ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#0ea5e9',
    600: '#0284c7',
    700: '#0369a1',
    800: '#075985',
    900: '#0c4a6e',
  },
  
  // Secondary Colors - Soft Pink
  secondary: {
    50: '#fdf2f8',
    100: '#fce7f3',
    200: '#fbcfe8',
    300: '#f9a8d4',
    400: '#f472b6',
    500: '#ec4899',
    600: '#db2777',
    700: '#be185d',
    800: '#9d174d',
    900: '#831843',
  },
  
  // Accent Colors - Soft Green
  accent: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },
  
  // Neutral Colors - Soft Grays
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
  },
  
  // Background Colors
  background: {
    default: '#fafbfc',
    paper: '#ffffff',
    card: '#ffffff',
    drawer: '#ffffff',
    chat: '#f8fafc',
  },
  
  // Text Colors
  text: {
    primary: '#1f2937',
    secondary: '#6b7280',
    disabled: '#9ca3af',
    inverse: '#ffffff',
  },
  
  // Status Colors
  success: {
    light: '#d1fae5',
    main: '#10b981',
    dark: '#059669',
  },
  
  warning: {
    light: '#fef3c7',
    main: '#f59e0b',
    dark: '#d97706',
  },
  
  error: {
    light: '#fee2e2',
    main: '#ef4444',
    dark: '#dc2626',
  },
  
  info: {
    light: '#dbeafe',
    main: '#3b82f6',
    dark: '#2563eb',
  },
  
  // Chat Colors
  chat: {
    user: {
      background: '#e0f2fe',
      text: '#0c4a6e',
      border: '#bae6fd',
    },
    assistant: {
      background: '#f8fafc',
      text: '#1f2937',
      border: '#e5e7eb',
    },
    welcome: {
      background: '#f0f9ff',
      text: '#0c4a6e',
      border: '#bae6fd',
    },
  },
  
  // Restaurant-specific color overrides (can be customized per restaurant)
  restaurant: {
    primary: '#0ea5e9', // Default primary
    secondary: '#ec4899', // Default secondary
    accent: '#22c55e', // Default accent
  }
};

// CSS Variables for easy customization
export const cssVariables = {
  '--color-primary-50': colors.primary[50],
  '--color-primary-100': colors.primary[100],
  '--color-primary-200': colors.primary[200],
  '--color-primary-300': colors.primary[300],
  '--color-primary-400': colors.primary[400],
  '--color-primary-500': colors.primary[500],
  '--color-primary-600': colors.primary[600],
  '--color-primary-700': colors.primary[700],
  '--color-primary-800': colors.primary[800],
  '--color-primary-900': colors.primary[900],
  
  '--color-secondary-50': colors.secondary[50],
  '--color-secondary-100': colors.secondary[100],
  '--color-secondary-200': colors.secondary[200],
  '--color-secondary-300': colors.secondary[300],
  '--color-secondary-400': colors.secondary[400],
  '--color-secondary-500': colors.secondary[500],
  '--color-secondary-600': colors.secondary[600],
  '--color-secondary-700': colors.secondary[700],
  '--color-secondary-800': colors.secondary[800],
  '--color-secondary-900': colors.secondary[900],
  
  '--color-accent-50': colors.accent[50],
  '--color-accent-100': colors.accent[100],
  '--color-accent-200': colors.accent[200],
  '--color-accent-300': colors.accent[300],
  '--color-accent-400': colors.accent[400],
  '--color-accent-500': colors.accent[500],
  '--color-accent-600': colors.accent[600],
  '--color-accent-700': colors.accent[700],
  '--color-accent-800': colors.accent[800],
  '--color-accent-900': colors.accent[900],
  
  '--color-neutral-50': colors.neutral[50],
  '--color-neutral-100': colors.neutral[100],
  '--color-neutral-200': colors.neutral[200],
  '--color-neutral-300': colors.neutral[300],
  '--color-neutral-400': colors.neutral[400],
  '--color-neutral-500': colors.neutral[500],
  '--color-neutral-600': colors.neutral[600],
  '--color-neutral-700': colors.neutral[700],
  '--color-neutral-800': colors.neutral[800],
  '--color-neutral-900': colors.neutral[900],
  
  '--color-background-default': colors.background.default,
  '--color-background-paper': colors.background.paper,
  '--color-background-card': colors.background.card,
  '--color-background-drawer': colors.background.drawer,
  '--color-background-chat': colors.background.chat,
  
  '--color-text-primary': colors.text.primary,
  '--color-text-secondary': colors.text.secondary,
  '--color-text-disabled': colors.text.disabled,
  '--color-text-inverse': colors.text.inverse,
  
  '--color-success-light': colors.success.light,
  '--color-success-main': colors.success.main,
  '--color-success-dark': colors.success.dark,
  
  '--color-warning-light': colors.warning.light,
  '--color-warning-main': colors.warning.main,
  '--color-warning-dark': colors.warning.dark,
  
  '--color-error-light': colors.error.light,
  '--color-error-main': colors.error.main,
  '--color-error-dark': colors.error.dark,
  
  '--color-info-light': colors.info.light,
  '--color-info-main': colors.info.main,
  '--color-info-dark': colors.info.dark,
  
  '--color-chat-user-background': colors.chat.user.background,
  '--color-chat-user-text': colors.chat.user.text,
  '--color-chat-user-border': colors.chat.user.border,
  
  '--color-chat-assistant-background': colors.chat.assistant.background,
  '--color-chat-assistant-text': colors.chat.assistant.text,
  '--color-chat-assistant-border': colors.chat.assistant.border,
  
  '--color-chat-welcome-background': colors.chat.welcome.background,
  '--color-chat-welcome-text': colors.chat.welcome.text,
  '--color-chat-welcome-border': colors.chat.welcome.border,
  
  '--color-restaurant-primary': colors.restaurant.primary,
  '--color-restaurant-secondary': colors.restaurant.secondary,
  '--color-restaurant-accent': colors.restaurant.accent,
};

// Function to apply restaurant-specific colors
export const applyRestaurantColors = (restaurantColors: {
  primary?: string;
  secondary?: string;
  accent?: string;
}) => {
  const root = document.documentElement;
  
  if (restaurantColors.primary) {
    root.style.setProperty('--color-restaurant-primary', restaurantColors.primary);
  }
  if (restaurantColors.secondary) {
    root.style.setProperty('--color-restaurant-secondary', restaurantColors.secondary);
  }
  if (restaurantColors.accent) {
    root.style.setProperty('--color-restaurant-accent', restaurantColors.accent);
  }
};

// Function to reset to default colors
export const resetRestaurantColors = () => {
  const root = document.documentElement;
  root.style.removeProperty('--color-restaurant-primary');
  root.style.removeProperty('--color-restaurant-secondary');
  root.style.removeProperty('--color-restaurant-accent');
};
